import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import { Edit, Block, CheckCircle } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { Category } from "../../../types/category";
import { fetchAllCategories } from "../../../State/customer/categorySlice";
import { updateCategory, toggleCategoryStatus } from "../../../State/admin/Action";

const CategoriesTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const { categories } = useAppSelector((state) => state.category);

  // Edit Modal State
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [editFormData, setEditFormData] = useState({ name: "", categoryId: "", level: 1 });
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  useEffect(() => {
    dispatch(fetchAllCategories());
  }, [dispatch]);

  const handleOpenEdit = (category: Category) => {
    setSelectedCategory(category);
    setEditFormData({
      name: category.name,
      categoryId: category.categoryId,
      level: category.level,
    });
    setOpenEdit(true);
  }; // Fixed: Removed trailing comma here

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedCategory(null);
  };

  const handleSaveUpdate = async () => {
    if (!selectedCategory?.id) return;
    try {
      await dispatch(
        updateCategory({ id: selectedCategory.id, data: editFormData })
      ).unwrap();
      dispatch(fetchAllCategories());
      setFeedback({ type: "success", msg: "Category updated successfully" });
      handleCloseEdit();
    } catch (err: any) {
      setFeedback({ type: "error", msg: err || "Failed to update category" });
    }
  };

  const handleToggleStatus = async (category: Category) => {
    if (!category.id) return;
    const newStatus = !category.disabled;
    try {
      await dispatch(
        toggleCategoryStatus({ id: category.id, disabled: newStatus })
      ).unwrap();
      dispatch(fetchAllCategories());
      setFeedback({
        type: "success",
        msg: `Category ${newStatus ? "disabled" : "enabled"} successfully`,
      });
    } catch (err: any) {
      setFeedback({ type: "error", msg: err || "Status update failed" });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" mb={2}>
        Categories Management
      </Typography>

      {feedback && (
        <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.msg}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={3}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              {/* Fixed: Moved fontWeight styling inside sx prop for TableCell */}
              <TableCell sx={{ fontWeight: "bold" }}>ID</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Slug (ID)</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Level</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((cat: Category) => (
              <TableRow key={cat.id || cat.categoryId} hover>
                <TableCell>{cat.id}</TableCell>
                <TableCell sx={{ fontWeight: "medium" }}>{cat.name}</TableCell>
                <TableCell>{cat.categoryId}</TableCell>
                <TableCell>
                  <Chip
                    label={`Level ${cat.level}`}
                    size="small"
                    color={cat.level === 1 ? "primary" : cat.level === 2 ? "secondary" : "default"}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    icon={cat.disabled ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
                    label={cat.disabled ? "Disabled" : "Active"}
                    color={cat.disabled ? "error" : "success"}
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit Category">
                    <IconButton color="primary" onClick={() => handleOpenEdit(cat)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={cat.disabled ? "Enable Category" : "Disable Category"}>
                    <Switch
                      checked={!cat.disabled}
                      onChange={() => handleToggleStatus(cat)}
                      color="success"
                      size="small"
                    />
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Category Dialog */}
      <Dialog open={openEdit} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: "bold" }}>Edit Category</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            margin="normal"
            label="Category Name"
            value={editFormData.name}
            onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Slug / Category ID"
            value={editFormData.categoryId}
            onChange={(e) => setEditFormData({ ...editFormData, categoryId: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Level</InputLabel>
            <Select
              value={editFormData.level}
              label="Level"
              onChange={(e) => setEditFormData({ ...editFormData, level: Number(e.target.value) })}
            >
              <MenuItem value={1}>Level 1 (Main)</MenuItem>
              <MenuItem value={2}>Level 2 (Subcategory)</MenuItem>
              <MenuItem value={3}>Level 3 (Item)</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveUpdate}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesTable;